import React, { useState, useEffect } from "react";
import AccountAPI from "../../services/AccountAPI";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelopeOpenText } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../common/LoadingSpinner";

/**
 * OrganizationSettings — account-owner controls that apply to the whole
 * organization (as opposed to the per-user preferences elsewhere in Settings).
 *
 * The accordion that hosts this component is already gated to corporate
 * account owners in Settings.js, so this component assumes an owner context.
 * It is intentionally a home for future organization-wide controls; for now it
 * holds the "Disable Inbound Email Analysis" data-governance toggle that was
 * previously buried in the Company Profile form.
 */
const OrganizationSettings = () => {
  const { t } = useTranslation();

  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    const loadAccount = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const response = await AccountAPI.getAccountByUserUuid();
        const accountInfo = response?.data || response;
        setAccountData(accountInfo || null);
      } catch (err) {
        console.error("OrganizationSettings: Error loading account", err);
        setLoadError(t("organization.error.load"));
      } finally {
        setLoading(false);
      }
    };
    loadAccount();
  }, [t]);

  const disableEmailAnalysis =
    (accountData?.disableInboundEmailAnalysis ??
      accountData?.disable_inbound_email_analysis) === true;

  const handleEmailAnalysisToggle = async () => {
    if (saving || !accountData) return;

    const newValue = !disableEmailAnalysis;
    const previous = accountData;

    // Optimistic update so the switch feels instant.
    setAccountData((prev) => ({
      ...prev,
      disableInboundEmailAnalysis: newValue,
    }));
    setSaveError(null);

    try {
      setSaving(true);
      // The company-profile endpoint is the authoritative writer for
      // account-level fields. Preserve the existing business demographics so
      // toggling this flag never clears them server-side.
      const payload = {
        name: accountData.name || "",
        description: accountData.description || "",
        industry: accountData.industry || "",
        employeeCount:
          accountData.employeeCount != null ? accountData.employeeCount : null,
        disableInboundEmailAnalysis: newValue,
      };
      const response = await AccountAPI.updateCompanyProfile(payload);
      const updated = response?.data || response;
      if (updated) {
        setAccountData(updated);
      }
    } catch (err) {
      console.error(
        "OrganizationSettings: Error updating inbound email analysis",
        err,
      );
      setAccountData(previous);
      setSaveError(
        err?.response?.data?.message || t("organization.error.save"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="common-form-group">
        <LoadingSpinner
          message={`${t("organization.dataGovernance.heading")}...`}
        />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="common-form-group">
        <p className="error-text" style={{ color: "#c0392b" }}>
          {loadError}
        </p>
      </div>
    );
  }

  return (
    <div className="common-form-group">
      <h4>{t("organization.dataGovernance.heading")}</h4>
      {saveError && (
        <p className="error-text" style={{ color: "#c0392b" }}>
          {saveError}
        </p>
      )}
      <div className="common-toggle-group indented-below-h4">
        <div className="common-toggle-info">
          <h4>
            <FontAwesomeIcon
              icon={faEnvelopeOpenText}
              style={{ marginRight: 6 }}
            />
            {t("organization.disableEmailAnalysis.title")}
          </h4>
          <p>{t("organization.disableEmailAnalysis.desc")}</p>
        </div>
        <div
          className={`common-toggle ${disableEmailAnalysis ? "active" : ""}`}
          onClick={handleEmailAnalysisToggle}
          role="switch"
          aria-checked={disableEmailAnalysis}
          aria-label={t("organization.disableEmailAnalysis.title")}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleEmailAnalysisToggle();
            }
          }}
          style={saving ? { opacity: 0.5, pointerEvents: "none" } : undefined}
        ></div>
      </div>
    </div>
  );
};

export default OrganizationSettings;
